import React, { useState } from 'react';
import { Modal } from '@ui-library/modalV2';
import { PasswordInput } from '@ui-library/input';

type usePasswordPromptOptions = {
  title?: string;
  onSubmit: (password: string) => void | Promise<void>;
};

export function usePasswordPrompt({
  title = 'Enter your password',
  onSubmit,
}: usePasswordPromptOptions) {
  const [isVisible, setIsVisible] = useState(false);
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toggleShow = () => {
    setPassword('');
    setIsVisible((prev) => !prev);
  };

  const handleOk = async () => {
    setIsSubmitting(true);
    try {
      await onSubmit(password);
      setPassword('');
      setIsVisible(false);
    } catch {
      // onSubmit surfaces the error on the page; just close the prompt
      setIsVisible(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalPlaceholder = (
    <Modal
      title={title}
      open={isVisible}
      onOk={handleOk}
      onCancel={toggleShow}
      okText="Submit"
      cancelText="Cancel"
      confirmLoading={isSubmitting}
      cancelButtonProps={{ disabled: isSubmitting }}
      destroyOnHidden
    >
      <PasswordInput
        placeholder="Enter your password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        onPressEnter={handleOk}
        disabled={isSubmitting}
      />
    </Modal>
  );

  return { modalPlaceholder, toggleShow };
}
